import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, fetchWithCredentials } from '../config/api';
import './PaymentPage.css';

const paymentMethods = [
  {
    id: 'usdt',
    name: 'USDT支付',
    icon: '₮',
    description: '使用USDT加密货币支付'
  },
  {
    id: 'wechat',
    name: '微信支付',
    icon: '💳',
    description: '使用微信扫码支付'
  },
  {
    id: 'alipay',
    name: '支付宝',
    icon: '💳',
    description: '使用支付宝扫码支付'
  },
  {
    id: 'paypal',
    name: 'PayPal',
    icon: '💳',
    description: '使用PayPal账户支付'
  }
];

function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [order, setOrder] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);

  const paymentMethods = [
    {
      id: 'paypal',
      name: 'PayPal',
      description: '使用PayPal安全支付',
      icon: '💳'
    },
    {
      id: 'usdt',
      name: 'USDT',
      description: '使用USDT加密货币支付',
      icon: '₮'
    },
    {
      id: 'wechat',
      name: '微信支付',
      description: '使用微信扫码支付',
      icon: '💚'
    },
    {
      id: 'alipay',
      name: '支付宝',
      description: '使用支付宝扫码支付',
      icon: '💙'
    }
  ];

  // 检查支付状态
  const checkPaymentStatus = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_ENDPOINTS.ORDERS.USDT_STATUS(orderId)}`);
      if (response && response.status) {
        setPaymentStatus(response.status);
        if (response.status === 'completed') {
          // 支付完成，清理定时器并跳转
          setStatusCheckInterval(prevInterval => {
            if (prevInterval) {
              clearInterval(prevInterval);
            }
            return null;
          });
          navigate('/payment/success', { state: { orderId } });
        } else if (response.status === 'expired') {
          // 支付过期，清理定时器
          setStatusCheckInterval(prevInterval => {
            if (prevInterval) {
              clearInterval(prevInterval);
            }
            return null;
          });
          setError('支付已過期，請重新創建訂單');
        }
      }
    } catch (err) {
      console.error('检查支付状态失败:', err);
    }
  }, [orderId, navigate]);

  // 创建USDT支付
  const createUSDTPayment = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchWithCredentials(API_ENDPOINTS.ORDERS.PAY(orderId), {
        method: 'POST',
        body: JSON.stringify({
          paymentMethod: 'usdt'
        })
      });

      if (response && response.paymentInfo) {
        setPaymentInfo(response.paymentInfo);
        setPaymentStatus('pending');
        // 开始定期检查支付状态
        const interval = setInterval(() => {
          checkPaymentStatus();
        }, 10000); // 每10秒检查一次
        setStatusCheckInterval(interval);
      } else {
        setError('創建USDT支付失敗，請稍後重試');
      }
    } catch (err) {
      setError(err.message || 'USDT支付處理失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  }, [orderId, checkPaymentStatus]);

  // 获取订单详情
  const fetchOrder = useCallback(async () => {
    try {
      const response = await fetchWithCredentials(`${API_ENDPOINTS.ORDERS.DETAIL(orderId)}`);
      if (response && response.order) {
        setOrder(response.order);
        
        // 如果订单支付方式是USDT且没有支付信息，自动创建USDT支付
        if (response.order.paymentMethod === 'usdt' && !response.order.paymentInfo) {
          console.log('订单支付方式为USDT，自动创建USDT支付');
          createUSDTPayment();
        }
      } else {
        setError('訂單不存在或已過期');
      }
    } catch (err) {
      setError('獲取訂單詳情失敗');
    }
  }, [orderId, createUSDTPayment]);

  // 初始化
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/auth', { state: { from: { pathname: `/payment/${orderId}` } } });
      return;
    }

    if (isLoggedIn && orderId) {
      fetchOrder();
    }
  }, [orderId, isLoggedIn, fetchOrder, navigate]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [statusCheckInterval]);

  const handlePayment = async () => {
    if (!selectedMethod) {
      setError('請選擇支付方式');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetchWithCredentials(API_ENDPOINTS.ORDERS.PAY(orderId), {
        method: 'POST',
        body: JSON.stringify({
          paymentMethod: selectedMethod
        })
      });

      if (response) {
        if (selectedMethod === 'usdt' && response.paymentInfo) {
          // USDT支付
          setPaymentInfo(response.paymentInfo);
          setPaymentStatus('pending');
          // 开始定期检查支付状态
          const interval = setInterval(() => {
            checkPaymentStatus();
          }, 10000); // 每10秒检查一次
          setStatusCheckInterval(interval);
        } else if (selectedMethod === 'paypal' && response.paymentUrl) {
          // PayPal支付
          window.location.href = response.paymentUrl;
        } else if (response.paymentUrl) {
          // 其他支付方式显示二维码
          setOrder(prev => ({
            ...prev,
            qrCode: response.paymentUrl
          }));
        } else {
          setError('創建支付失敗，請稍後重試');
        }
      } else {
        setError('創建支付失敗，請稍後重試');
      }
    } catch (err) {
      setError(err.message || '支付處理失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('地址已複製到剪貼板');
    }).catch(() => {
      alert('複製失敗，請手動複製');
    });
  };

  if (!order) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="loading">加載中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="container">
        <h1>支付訂單</h1>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="order-summary">
          <h2>訂單摘要</h2>
          <div className="summary-details">
            <p>訂單號：{order.orderNumber}</p>
            <p>金額：${order.amount} {order.currency}</p>
            <p>狀態：{order.status}</p>
            {order.paymentMethod && (
              <p>支付方式：{paymentMethods.find(m => m.id === order.paymentMethod)?.name || order.paymentMethod}</p>
            )}
          </div>
        </div>

        {/* 如果订单没有支付方式，显示支付方式选择 */}
        {!order.paymentMethod && !paymentInfo && (
          <div className="payment-methods">
            <h2>選擇支付方式</h2>
            <div className="methods-grid">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`method-card ${selectedMethod === method.id ? 'selected' : ''}`}
                  onClick={() => setSelectedMethod(method.id)}
                >
                  <div className="method-icon">{method.icon}</div>
                  <h3>{method.name}</h3>
                  <p>{method.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 如果订单选择了USDT支付，直接显示USDT支付信息 */}
        {order.paymentMethod === 'usdt' && !paymentInfo && (
          <div className="loading-section">
            <div className="loading-indicator">
              <div className="spinner"></div>
              <span>正在生成USDT支付信息...</span>
            </div>
          </div>
        )}

        {paymentInfo && (
          <div className="usdt-payment-section">
            <h2>USDT支付</h2>
            <div className="usdt-payment-info">
              <div className="wallet-info">
                <h3>收款地址</h3>
                <div className="wallet-address">
                  <span>{paymentInfo.walletAddress}</span>
                  <button 
                    className="copy-button"
                    onClick={() => copyToClipboard(paymentInfo.walletAddress)}
                  >
                    複製
                  </button>
                </div>
                <p className="network-info">網絡：{paymentInfo.network}</p>
              </div>
              
              <div className="amount-info">
                <h3>支付金額</h3>
                <p className="amount">{paymentInfo.amount} USDT</p>
                <p className="order-number">訂單號：{paymentInfo.orderNumber}</p>
                <p style={{color: 'red', fontWeight: 'bold', marginTop: 8}}>
                  請務必支付精確到小數點後4位的金額，否則無法自動識別訂單！
                </p>
              </div>

              <div className="qr-code-section">
                <h3>掃碼支付</h3>
                <div className="qr-code">
                  <img src={paymentInfo.qrCode} alt="USDT錢包地址二維碼" />
                </div>
              </div>

              <div className="instructions">
                <h3>支付說明</h3>
                <ul>
                  {paymentInfo.instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ul>
              </div>

              <div className="payment-status">
                <p>支付狀態：{paymentStatus === 'pending' ? '等待支付確認' : paymentStatus}</p>
                {paymentStatus === 'pending' && (
                  <>
                    <div style={{color: 'red', fontWeight: 'bold', marginBottom: 8}}>請勿關閉或刷新當前頁面</div>
                    <div className="loading-indicator">
                      <div className="spinner"></div>
                      <span>正在檢查支付狀態...</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {order.qrCode && (
          <div className="qr-code-section">
            <h2>請掃碼支付</h2>
            <div className="qr-code">
              <img src={order.qrCode} alt="支付二維碼" />
            </div>
            <p className="qr-tip">請使用{selectedMethod === 'wechat' ? '微信' : '支付寶'}掃描二維碼完成支付</p>
          </div>
        )}

        {/* 只有在没有支付方式且没有支付信息时才显示确认支付按钮 */}
        {!order.paymentMethod && !paymentInfo && !order.qrCode && (
          <button
            className="pay-button"
            onClick={handlePayment}
            disabled={loading || !selectedMethod}
          >
            {loading ? '處理中...' : '確認支付'}
          </button>
        )}
      </div>
    </div>
  );
}

export default PaymentPage; 